import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { memo } from 'react';

import { PlusNavObj } from '../types';

interface ComponentViewWrapperProps {
  SelectedComponent: PlusNavObj;
  handleBackClick: () => void;
  handleClose: () => void;
}

const ComponentViewWrapper = memo(
  ({ SelectedComponent, handleBackClick, handleClose }: ComponentViewWrapperProps) => {
    const theme = useTheme();
    const RenderComponent = SelectedComponent?.component;
    const title = SelectedComponent?.label;
    const showTitle = SelectedComponent?.showTitle;

    if (!RenderComponent) {
      return null;
    }

    return (
      <Box
        id="plus-button-component-view"
        data-testid="plus-button-component-view"
        sx={{
          display: 'grid',
          gridTemplateAreas: `
              "header"
              "content"
            `,
          gridTemplateRows: 'auto 1fr',
          height: '100%',
          width: '100%',
        }}
      >
        <Stack
          id="plus-button-component-header"
          data-testid="plus-button-component-header"
          direction="row"
          sx={{
            justifyContent: 'space-between',
            gridArea: 'header',
            borderBottom: `1px solid ${theme.palette.divider}`,
            p: 1,
            alignItems: 'center',
          }}
        >
          <Tooltip
            title="Back"
            id="plus-button-component-back"
            data-testid="plus-button-component-back"
            disableInteractive
          >
            <IconButton onClick={handleBackClick}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          {showTitle && (
            <Typography
              variant="h6"
              id="plus-button-component-title"
              data-testid="plus-button-component-title"
              sx={(theme) => ({
                color:
                  theme.palette.mode === 'light'
                    ? theme.blox.colors.bluegray
                    : theme.blox.colors.white,
                fontWeight: 500,
              })}
            >
              {title}
            </Typography>
          )}

          <Tooltip
            title="Close"
            id="plus-button-component-close"
            data-testid="plus-button-component-close"
            disableInteractive
          >
            <IconButton onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Box
          id="plus-button-component-content"
          data-testid="plus-button-component-content"
          sx={{
            gridArea: 'content',
            overflow: 'auto',
            my: 2,
            px: 2,
          }}
        >
          <RenderComponent handleClose={handleClose} />
        </Box>
      </Box>
    );
  },
);

ComponentViewWrapper.displayName = 'ComponentViewWrapper';
export default ComponentViewWrapper;
